import { ChangeDetectionStrategy, Component, WritableSignal, signal } from '@angular/core';
import { LogDb } from 'lib/cosmos/log/logDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { linkedItemsHelper } from 'lib/helpers/linked-items-helper.class.js';
import { ITable } from 'lib/ui/table/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

@Component({
	templateUrl: './list-emails.component.html',
	selector: 'mod-list-emails',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ListEmailsComponent extends TypeCoreComponent {
	public readonly table: WritableSignal<ITable<LogDb.ILogItem> | undefined> = signal(undefined);

	constructor() {
		super();

		this.dataInit = () => {
			this.initLogs();
		};
	}

	private initLogs(): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<LogDb.ILogItem[]>((builder) => builder.withCustomApiRoute('log', 'latestEmails').build())
				.pipe(
					map((response) => {
						this.table.set({
							itemType: 'Log',
							search: {
								type: 'local'
							},
							noItemsText: {
								noItems: `There are no email logs`,
								noFilteredItems: `No email logs match your filter`
							},
							items: response.data,
							id: (item) => item.id,
							title: {
								title: 'Emails',
								value: (item) => item.message
							},
							avatar: (item) => {
								const user = linkedItemsHelper
									.getLinkedItemsByType<UserDb.IUserItem>(response.linkedItems ?? [], 'User')
									.find((m) => m.email === item.email);

								let avatar: IImage | undefined;

								if (user?.avatar) {
									avatar = user.avatar;
								} else if (user?.browser?.isBot) {
									avatar = this.appContext()?.appSetup.botImage?.[0]?.image;
								}

								return {
									image: avatar,
									name: item.message
								};
							},
							subtitle: (item) => `${item.details ?? ''}`,
							mainItemClick: (item) => {
								const user = linkedItemsHelper
									.getLinkedItemsByType<UserDb.IUserItem>(response.linkedItems ?? [], 'User')
									.find((m) => m.email === item.email);

								this.dependencies.dialogService.openTextDialog({
									title: `Email log details`,
									text: item.message,
									avatar: user?.avatar,
									tableItems: [
										{
											iconType: 'date',
											title: 'Date',
											value: `${super.formatDateTime(item.system.created)}`,
											valueType: 'text'
										},
										{
											iconType: 'relativeTime',
											title: 'From now',
											value: `${super.fromNow(item.system.created)}`,
											valueType: 'text'
										},
										{
											iconType: 'user',
											title: 'E-mail',
											value: item.email,
											valueType: 'text'
										},
										{
											iconType: 'notification',
											title: 'Behavior',
											value: item.behavior,
											valueType: 'text'
										},
										{
											iconType: 'log',
											title: 'Log type',
											value: item.logType,
											valueType: 'text'
										},
										{
											iconType: 'info',
											title: 'Source',
											value: item.source,
											valueType: 'text'
										},
										{
											iconType: 'link',
											title: 'Url',
											value: item.url,
											valueType: 'text'
										},
										{
											iconType: 'browser',
											title: 'Browser',
											value: JSON.stringify(item.browser),
											valueType: 'text'
										},
										{
											iconType: 'text',
											title: 'Details',
											value: item.details,
											valueType: 'html'
										},
										{
											iconType: 'error',
											title: 'Stacktrace',
											value: item.stacktrace,
											valueType: 'html'
										}
									]
								});
							},
							fields: [
								{
									format: 'text',
									title: `Date`,
									value: (item) => this.dependencies.timeService.fromNow(item.system.updated),
									sortBy: (item) => new Date(item.system.updated),
									isDefaultSort: true,
									showOnMobiles: true
								}
							]
						});
					})
				)
		);
	}
}
